import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X, Trash2, Plus, Minus, Cog } from 'lucide-react';
import Select from 'react-select';
import { withRole } from '../Common/withRole';
import { getEquipment } from '../../services/supabase';
import { DefaultButton, PrimaryButton, IconButton } from '../Common/BaseButton';

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
`;
const ModalContent = styled.div`
  background: #fff; border-radius: 10px; padding: 2rem; min-width: 350px; max-width: 95vw;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  position: relative;
`;
const CloseButton = styled.button`
  position: absolute; 
  top: 1rem; 
  right: 1rem; 
  background: none; 
  border: none; 
  cursor: pointer;
`;
const EquipmentList = styled.div`
  margin-bottom: 1.5rem;
  max-height: 300px;
  overflow-y: auto;
`;
const EquipmentRow = styled.div`
  display: flex; 
  align-items: center; 
  gap: 0.5rem; 
  margin-bottom: 0.7rem;
  background: #f8f9fa; 
  border-radius: 6px; 
  padding: 0.5rem 0.7rem;
`;
const Name = styled.div`
  flex: 1; 
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  font-weight: 500;
`;
const QuantityInput = styled.input`
  width: 48px; 
  text-align: center; 
  font-size: 1rem; 
  border: 1px solid #ccc; 
  border-radius: 6px;
  margin: 0 0.3rem;
`;
const DeleteButton = styled(IconButton)`
  color: #e74c3c;
    &:hover:not(:disabled) {
    background-color:rgb(246, 204, 199);
  }
`;
const DecrementButton = styled(IconButton)`
    &:hover:not(:disabled) {
        background-color:rgb(246, 204, 199);
    }
`;
const AddSection = styled.div`
  margin-top: 1.5rem;
`;
const SaveRow = styled.div`
  display: flex; justify-content: flex-end; gap: 0.7rem; margin-top: 1.5rem;
`;

const ManageEventEquipmentButtonWrapper = styled.div`
  margin-top: 12px;
  width: fit-content;
  @media (max-width: 768px) {
    width: 100%;
  }
`;

function getEquipmentOptions(allEquipment, currentEquipment) {
  const usedIds = new Set(currentEquipment.map(eq => eq.equipment.id));
  return allEquipment
    .filter(eq => !usedIds.has(eq.id))
    .map(eq => ({ value: eq.id, label: eq.name }));
}

const ManageEventEquipmentButton = ({
  eventId,
  currentEquipment = [],
  onEquipmentChange,
  isManager,
  isAuthenticated
}) => {
  const [open, setOpen] = useState(false);
  const [addValue, setAddValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allEquipment, setAllEquipment] = useState([]);
  const [localEquipment, setLocalEquipment] = useState([]);

  useEffect(() => {
    if (open) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scrolling when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const handleOpen = () => {
    setLocalEquipment(currentEquipment.map(eq => ({
      ...eq,
      quantity: eq.quantity || 1
    })));
    setOpen(true);
    getEquipment().then(({ data }) => setAllEquipment(data || []));
  };

  // Handlers
  const handleQuantityChange = (idx, value) => {
    setLocalEquipment(eqs =>
      eqs.map((eq, i) =>
        i === idx
          ? { ...eq, quantity: Math.max(1, Number(value) || 1) }
          : eq
      )
    );
  };
  const handleIncrement = idx => {
    setLocalEquipment(eqs =>
      eqs.map((eq, i) =>
        i === idx ? { ...eq, quantity: (eq.quantity || 1) + 1 } : eq
      )
    );
  };
  const handleDecrement = idx => {
    setLocalEquipment(eqs =>
      eqs.map((eq, i) =>
        i === idx ? { ...eq, quantity: Math.max(1, (eq.quantity || 1) - 1) } : eq
      )
    );
  };
  const handleDelete = idx => {
    setLocalEquipment(eqs => eqs.filter((_, i) => i !== idx));
  };
  const handleAddEquipment = selected => {
    if (!selected) return;
    const eq = allEquipment.find(e => e.id === selected.value);
    if (!eq) return;
    setLocalEquipment(eqs => [
      ...eqs,
      {
        id: null, // Not yet in DB
        event_id: eventId,
        equipment_id: eq.id,
        equipment: eq,
        quantity: 1
      }
    ]);
    setAddValue(null);
  };

  // Save: full update (delete missing, update changed, add new)
  const handleSave = async () => {
    setSaving(true);
    try {
      if (onEquipmentChange) {
        await onEquipmentChange(localEquipment);
      }
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ManageEventEquipmentButtonWrapper>
        <PrimaryButton type="button" onClick={handleOpen} style={{width: '100%'}}>
          <Cog size={16} /> Manage Equipment
        </PrimaryButton>
      </ManageEventEquipmentButtonWrapper>
      {open && createPortal(
        <ModalOverlay>
          <ModalContent>
            <CloseButton onClick={() => setOpen(false)}><X size={20} /></CloseButton>
            <h3 style={{marginTop: 0}}> Manage Event Equipment</h3>
            <EquipmentList>
              {localEquipment.length === 0 ? (
                <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>
                  No equipment assigned for this event.
                </div>
              ) : (
                localEquipment.map((eq, idx) => (
                  <EquipmentRow key={eq.equipment_id || eq.equipment.id}>
                    <Name>{eq.equipment?.name || 'Unknown'}</Name>
                    <DecrementButton
                      type="button"
                      onClick={() => handleDecrement(idx)}
                      disabled={eq.quantity <= 1}
                      title="Decrease"
                    >
                      <Minus size={16} />
                    </DecrementButton>
                    <QuantityInput
                      type="number"
                      min={1}
                      value={eq.quantity}
                      onChange={e => handleQuantityChange(idx, e.target.value)}
                    />
                    <IconButton
                      type="button"
                      onClick={() => handleIncrement(idx)}
                      title="Increase"
                    >
                      <Plus size={16} />
                    </IconButton>
                    <DeleteButton
                      type="button"
                      onClick={() => handleDelete(idx)}
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </DeleteButton>
                  </EquipmentRow>
                ))
              )}
            </EquipmentList>
            <AddSection>
              <Select
                value={addValue}
                onChange={handleAddEquipment}
                options={getEquipmentOptions(allEquipment, localEquipment)}
                placeholder="Add equipment..."
                isClearable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: 38,
                    borderColor: state.isFocused ? '#2D882D' : '#ddd',
                    boxShadow: state.isFocused ? '0 0 0 1px #2D882D' : 'none',
                    '&:hover': {
                      borderColor: '#2D882D'
                    },
                    borderRadius: 8,
                  }),
                  menu: provided => ({
                    ...provided,
                    zIndex: 9999,
                    borderRadius: 8,
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? '#2D882D'
                      : state.isFocused
                      ? 'rgba(45, 136, 45, 0.1)'
                      : 'white',
                    color: state.isSelected ? 'white' : '#333',
                    cursor: 'pointer',
                  }),
                  placeholder: provided => ({
                    ...provided,
                    color: '#888',
                  }),
                  singleValue: provided => ({
                    ...provided,
                    color: '#333',
                  }),
                  multiValue: provided => ({
                    ...provided,
                    backgroundColor: '#e0e0e0',
                  }),
                  multiValueLabel: provided => ({
                    ...provided,
                    color: '#333',
                  }),
                  multiValueRemove: provided => ({
                    ...provided,
                    color: '#e74c3c',
                    ':hover': {
                      backgroundColor: '#f6ccc7',
                      color: '#b91c1c',
                    },
                  }),
                }}
              />
            </AddSection>
            <SaveRow>
              <DefaultButton type="button" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </DefaultButton>
              <PrimaryButton type="button" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </PrimaryButton>
            </SaveRow>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </>
  );
};

export default withRole(ManageEventEquipmentButton, { managerOnly: true });